import {
  ActivityFilterKey,
  FeatureName,
  useIsFeatureEnabled,
} from '@gripp/shared-logic';
import {
  ActivityList as ActivityListBase,
  CampaignAggregatedActivityData,
  CampaignAggregatedCard,
  IntegrationDigestRow,
} from '@gripp/shared-ui';
import { Pressable, StyleSheet, View } from 'react-native';
import { useNavigate } from 'react-router-dom';
import { IntegrationDigestSection } from './integrationDigestSection';

export type ActivitiesProps = {
  assetId?: string;
  issueId?: string;
  onIssueSelected?: (id: string) => void;
  showAssetName?: boolean;
  selectedIssue?: string;
  onActivityCountChange?: (count: number) => void;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  onDataLoaded?: (data: any) => void;
  hideFilterBar?: boolean;
  /**
   * PR 4 / B3 — polymorphic feed routing.
   *
   * Pre-fetched campaign aggregated rows (`campaignAggregatedActivities`).
   * Rendered above the issue list via `CampaignAggregatedCard`.  The merge
   * is two-tier rather than fully interleaved because the issue list is
   * paginated via `useInfiniteFindByQuery` inside `ActivityListBase` and we
   * cannot peek at successive pages without bringing the data fetch into
   * this component.  Campaign rows are unpaginated (server returns the
   * full set per `CampaignAggregatedActivitiesResult`), so they live in a
   * single top section sorted by `latestActivityAt` desc.
   *
   * Discriminator: presence of `campaignId` (vs. `Issue.id` rows from
   * `findIssues`).  No `__typename` mixing — the two queries are kept in
   * separate rendering passes.
   */
  campaignRows?: CampaignAggregatedActivityData[];
  /**
   * GeoTab integration daily sync digest rows (GRIPP #2887). Rendered above the
   * campaign section as `IntegrationDigestSection`. Same separate-pass approach
   * as `campaignRows` — never interleaved with paginated issue rows, never on
   * the per-asset feed.
   */
  digestRows?: IntegrationDigestRow[];
  /**
   * digestKey of the digest whose detail panel is currently open, for selected-
   * state styling on the corresponding feed row.
   */
  selectedDigestKey?: string;
};

export const ActivityList = (props: ActivitiesProps) => {
  const isCropCircleEnabled = useIsFeatureEnabled(
    FeatureName.CropCircleRedesign
  );

  const filterKey = props.assetId
    ? ActivityFilterKey.AssetActivity
    : ActivityFilterKey.Activity;

  // Campaign aggregated rows only render on the global activity feed
  // (no `assetId`).  On the per-asset detail page the campaign Issues are
  // rendered as ordinary issue rows with the pink CAMPAIGN variant
  // (PR 4 / C1) — aggregation would double-count them.
  const showCampaignSection =
    !props.assetId &&
    isCropCircleEnabled &&
    props.campaignRows !== undefined &&
    props.campaignRows.length > 0;

  // Integration digest card renders only on the global feed (no `assetId`),
  // in Crop Circle mode, when at least one digest row is present. Mirrors the
  // campaign-section gating.
  const showDigestSection =
    !props.assetId &&
    isCropCircleEnabled &&
    props.digestRows !== undefined &&
    props.digestRows.length > 0;

  return (
    <View style={styles.container}>
      {showDigestSection && (
        <IntegrationDigestSection
          rows={props.digestRows ?? []}
          selectedDigestKey={props.selectedDigestKey}
        />
      )}
      {showCampaignSection && (
        <CampaignRowsSection
          rows={props.campaignRows ?? []}
          selectedIssue={props.selectedIssue}
        />
      )}
      <ActivityListBase
        showScrollIndicator={true}
        containerStyle={styles.container}
        filterKey={filterKey}
        showTopBar={!isCropCircleEnabled}
        {...props}
      />
    </View>
  );
};

type CampaignRowsSectionProps = {
  rows: CampaignAggregatedActivityData[];
  selectedIssue?: string;
};

/**
 * Renders the top "campaign aggregated" section of the feed.
 *
 * Click navigation: routes to the OEM `/campaigns` page with the selected
 * campaign id in `location.state`, which opens the Responses panel for that
 * campaign. While multi-workspace assets are missing the resolver is open to
 * OEM viewers too (see campaignAggregatedActivities TEMPORARY note), so the
 * same target works in both feed contexts. When a Dealer-side campaign
 * thread route lands, branch on workspace type here.
 */
const CampaignRowsSection = ({
  rows,
  selectedIssue,
}: CampaignRowsSectionProps) => {
  const navigate = useNavigate();
  return (
    <View style={styles.campaignSection}>
      {rows.map((row) => (
        <Pressable
          key={row.campaignId}
          testID={`activity-row-campaign-${row.campaignId}`}
          style={styles.campaignRow}
          accessibilityRole="button"
          accessibilityLabel={`Open campaign ${row.title}`}
          onPress={() =>
            navigate('/campaigns', {
              state: { selectedCampaignId: row.campaignId },
            })
          }
        >
          <CampaignAggregatedCard
            activity={row}
            isSelected={selectedIssue === row.campaignId}
          />
        </Pressable>
      ))}
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    overflow: 'hidden',
    borderRadius: 0,
    flex: 1,
  },
  campaignSection: {
    // Row separators are drawn by `ActivityCard` itself, so the section
    // wrapper stays a layout-only container.  No background — the
    // surrounding column already paints brand white.
  },
  campaignRow: {},
});
